import {
  faGavel,
  faHourglassHalf,
  faHourglassStart,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { addHours, isBefore } from 'date-fns'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  Countdown,
  Duration,
  FormattedMessage,
  InteractiveIcon,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import type { SearchResult } from '@/queries/listingSearchQuery'

import useBuyNowEndTimeIfActive from '../useBuyNowEndTime'

export const TimeLeftRowContainer = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
`

const Divider = styled.span`
  height: 0.5rem;
  width: 0.0625rem;
  background: ${Colors.Semantic.Neutral.very_dark_grey};
`

const IconAndValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
`

const TimeLeftAndPopcornBlock = styled(IconAndValue)<{ $isHot: boolean }>`
  width: 100%;
  max-width: max-content;
  overflow: hidden;
  flex-shrink: 1;
  color: ${({ $isHot }) =>
    $isHot ? Colors.Semantic.Warning.highlight : 'inherit'};
`

const NPopcornBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 1;
  width: 100%;
  max-width: max-content;
  overflow: hidden;
`

const NPopcornLabel = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  flex-shrink: 1;
  width: 100%;
  max-width: max-content;
`

const TimeLeftAndBidCount = ({
  listing,
  endTime,
}: {
  listing: SearchResult
  endTime: Date
}) => {
  const buyNowEndTime = useBuyNowEndTimeIfActive(listing)
  const endsWithinTheHour = isBefore(endTime, addHours(Date.now(), 1)) // oxlint-disable-line react/purity
  const hasEnded = isBefore(endTime, Date.now()) // oxlint-disable-line react/purity

  return (
    <TimeLeftRowContainer>
      <TimeLeftAndPopcornBlock $isHot={endsWithinTheHour}>
        <FontAwesomeIcon
          icon={endsWithinTheHour ? faHourglassHalf : faHourglassStart}
          size="sm"
        />
        <div data-testid="time-left">
          {endsWithinTheHour ? (
            // If we have less than an hour, we omit the "left" suffix.
            // This is done primarily to make room for Popcorn info,
            // but we do it for everything that is "hot" even though
            // strictly speaking we don't need that space currently
            // if not showing the "+N popcorn" content
            <Duration target={endTime} format="short" />
          ) : (
            // If we have more than an hour left, show the full
            // countdown,  including the "left" suffix (we know
            // we'll have space, since it definitely won't be
            // in popcorn bidding time)
            <Countdown target={endTime} />
          )}
        </div>
        {listing.nPopcorn &&
        !hasEnded &&
        listing.pricingStrategy === 'AUCTION' &&
        !buyNowEndTime ? (
          <NPopcornBlock data-testid="popcorn">
            <NPopcornLabel>
              <FormattedMessage
                id="SearchResultCard.popcorn"
                values={{ amount: listing.nPopcorn }}
              />
            </NPopcornLabel>
            <InteractiveIcon>
              <FormattedMessage id="Tooltip.Content.popcornBidding" />
            </InteractiveIcon>
          </NPopcornBlock>
        ) : null}
      </TimeLeftAndPopcornBlock>
      {listing.pricingStrategy === 'AUCTION' && !buyNowEndTime ? (
        // Show "Number of Bids" only if it is an auction, and we're not
        // within a "Buy Now" period.
        <>
          <Divider />
          <IconAndValue>
            <FontAwesomeIcon icon={faGavel} size="sm" />
            <div data-testid="number-of-bids">
              <FormattedNumber value={listing.numberOfBids || 0} />
            </div>
          </IconAndValue>
        </>
      ) : null}
    </TimeLeftRowContainer>
  )
}

export default TimeLeftAndBidCount
